import React from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faMagic, faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Button, DashboardModule,
} from '@domain-group/suit-theme'

import { useFilters } from '@domain-group/react-hooks'

import usePublicationEdition from '@hooks/usePublicationEdition'
import usePublicationEditions from '@hooks/usePublicationEditions'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import PublicationEditionsTable from './PublicationEditionsTable'

const oneMonthAgo = DateTime.now().minus({ months: 1 })

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        dateFormat: 'DD/MM/YYYY',
        dateParseFormat: 'YYYY-MM-DD',
        fieldName: 'min_publication_date',
        inputType: 'date',
        style,
      },
      key: 'min_publication_date',
      label: '* Min Publication Date',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputType: 'date',
        style,
      },
      key: 'max_publication_date',
      label: 'Max Publication Date',
    },
  ]
}

const PublicationEditions = (props) => {
  const { publicationId } = props

  const defaultFilters = { min_publication_date: oneMonthAgo.toFormat('yyyy-MM-dd') }

  const filterPayload = useFilters({ defaultFilters })
  const { pageFilters } = filterPayload

  const publicationEditionsPayload = usePublicationEditions({
    filters: { publication_id: publicationId, ...pageFilters },
    performHttpRequests: !!publicationId && !!pageFilters.min_publication_date,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationEditions,
    filteredPublicationsEditionsCount,
    hasFilteredPublicationEditions,
    loading,
  } = publicationEditionsPayload

  const {
    callbacks: { autoGenerate, createOrEditPublicationEdition, rebuildDeadlines },
    creating,
    updating,
  } = usePublicationEdition({ publication_id: publicationId })

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-editions' }}
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Editions"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      {!loading && hasFilteredPublicationEditions && (
        <PublicationEditionsTable publicationEditions={filteredPublicationEditions} />
      )}

      {!loading && !hasFilteredPublicationEditions && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Editions Present
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please change your filters or add an edition below
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      <DashboardModule.LoadMoreFooter
        boxProps={{
          borderTop: loading ? 'none' : '1px solid',
          flexDirection: ['column', 'row'],
        }}
        callbacks={{ loadMore }}
        canLoadMore={canLoadMore}
        entityCount={filteredPublicationsEditionsCount}
        loading={loading}
      >
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faCalendarAlt} />}
          loading={updating}
          onClick={() => rebuildDeadlines()}
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Rebuild Deadlines
        </Button>

        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faMagic} />}
          loading={creating}
          marginLeft={['none', 'medium']}
          marginTop={['medium', 0]}
          onClick={() => autoGenerate()}
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Generate Editions
        </Button>

        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginLeft={['none', 'medium']}
          marginTop={['medium', 0]}
          onClick={() => createOrEditPublicationEdition()}
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add Edition
        </Button>
      </DashboardModule.LoadMoreFooter>
    </DashboardModule>
  )
}

PublicationEditions.propTypes = {
  publicationId: PropTypes.number.isRequired,
}

export default PublicationEditions
